import {
  Avatar,
  Badge,
  BlockStack,
  Card,
  Grid,
  IndexTable,
  InlineStack,
  Page,
  Text,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame, type ChromeUser} from '@slj/ui/app-frame';
import {LetterTile} from '@slj/ui/brand';
import {CardLink, EmptyState} from '@slj/ui/patterns';

import {identityAccount, identitySections} from './nav';

export interface DashboardApp {
  name: string;
  origin: string;
  /** Absolute URL of the app's own mark, or null → fall back to its initial. Resolved
   *  route-side via appIconUrl(); window.__PAGE__ is JSON, so this cannot be a component. */
  iconUrl?: string | null;
}

export interface ActivityRow {
  at: string;
  label: string;
  tone: 'critical' | 'attention' | 'success' | undefined;
  ip: string | null;
}

export interface ChromeProps {
  user: ChromeUser;
  path: string;
}

export interface DashboardProps extends PagePropsBase {
  chrome: ChromeProps;
  memberSince: string;
  apps: DashboardApp[];
  activity: ActivityRow[];
}

/**
 * The signed-in home at auth.slj.app: an identity card, the app launcher fed by the
 * platform registry (filtered to the apps this user may open), and recent security
 * activity.
 *
 * NOT StaticBody: Polaris `Avatar` only paints its image after mount (an
 * `isAfterInitialMount` effect), so a non-hydrated body leaves the photo a grey disc.
 * The page is small (one card, a short grid, ≤20 activity rows), so hydrating it is cheap.
 */
export function DashboardPage({chrome, memberSince, apps, activity}: DashboardProps) {
  const {user} = chrome;
  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page>
        <BlockStack gap="500">
          <Card roundedAbove="sm">
            <InlineStack gap="500" blockAlign="center" wrap={false}>
              <Avatar
                size="xl"
                name={user.name}
                source={user.avatarUrl ?? undefined}
                initials={user.name.slice(0, 1).toUpperCase()}
              />
              <BlockStack gap="150">
                <InlineStack gap="200" blockAlign="center" wrap>
                  <Text as="h1" variant="headingLg">
                    {user.name}
                  </Text>
                  {user.isAdmin ? <Badge tone="attention">Admin</Badge> : null}
                </InlineStack>
                <Text as="p" tone="subdued">
                  {user.email}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {`Member since ${memberSince}`}
                </Text>
              </BlockStack>
            </InlineStack>
          </Card>

          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Apps
            </Text>
            {apps.length === 0 ? (
              <Card>
                <EmptyState>No apps yet — ask an admin for access.</EmptyState>
              </Card>
            ) : (
              <Grid>
                {apps.map((app) => (
                  <Grid.Cell key={app.origin} columnSpan={{xs: 6, sm: 3, md: 3, lg: 4, xl: 4}}>
                    <CardLink href={app.origin}>
                      <Card roundedAbove="sm">
                        <InlineStack gap="300" blockAlign="center" wrap={false}>
                          {app.iconUrl ? (
                            <img
                              src={app.iconUrl}
                              alt=""
                              width={40}
                              height={40}
                              style={{borderRadius: 'var(--p-border-radius-200)', display: 'block'}}
                            />
                          ) : (
                            <LetterTile letter={app.name.slice(0, 1).toUpperCase()} size={40} />
                          )}
                          <BlockStack gap="050">
                            <Text as="h3" variant="headingSm">
                              {app.name}
                            </Text>
                            <Text as="span" tone="subdued" variant="bodySm">
                              {new URL(app.origin).host}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                      </Card>
                    </CardLink>
                  </Grid.Cell>
                ))}
              </Grid>
            )}
          </BlockStack>

          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Recent activity
            </Text>
            <Card padding="0">
              {activity.length === 0 ? (
                <EmptyState>Nothing yet.</EmptyState>
              ) : (
                <IndexTable
                  resourceName={{singular: 'event', plural: 'events'}}
                  itemCount={activity.length}
                  selectable={false}
                  headings={[{title: 'What'}, {title: 'When'}, {title: 'From'}]}
                >
                  {activity.map((a, index) => (
                    <IndexTable.Row id={`${index}`} key={index} position={index}>
                      <IndexTable.Cell>
                        <Badge tone={a.tone}>{a.label}</Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text as="span" tone="subdued">{a.at}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text as="span" tone="subdued">{a.ip ?? '—'}</Text>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              )}
            </Card>
          </BlockStack>
        </BlockStack>
      </Page>
    </AppFrame>
  );
}
